import tissue from "@/data/tissue.json";
import { PageHero } from "@/components/PageHero";
import { ProductCard } from "@/components/ProductCard";
import { Reveal, Marquee } from "@/components/motion";
import { useLang } from "@/context/LangContext";

const items = tissue.map((t) => ({
  id: `tc-${String(t.n).padStart(2, "0")}`,
  name: `${String(t.n).padStart(2, "0")}. ${t.name}`,
  price: t.price === "TBC" ? "To Be Confirmed" : `${t.price} (TC)`,
  img: t.img,
  code: t.code,
  contain: true,
}));

const Explainer = () => {
  const { t } = useLang();
  return (
  <section className="bg-white py-20 sm:py-28">
    <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <div className="relative aspect-[4/3] overflow-hidden rounded-[2rem] bg-[var(--mint)]">
          <img src="/assets/img/tc-hand.jpg" alt={t("Tanaman kultur jaringan di telapak tangan", "Tissue culture plant in hand")} className="h-full w-full object-cover" />
        </div>
      </Reveal>
      <Reveal className="lg:col-span-7" delay={0.1}>
        <p className="eyebrow">What is Tissue Culture?</p>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1]">{t("Bibit bersih dari laboratorium steril.", "Clean plantlets from a sterile laboratory.")}</h2>
        <p className="mt-6 text-[var(--muted-ink)] leading-relaxed">{t("Minaqu Indonesia adalah supplier tanaman tropis hias dari Indonesia yang dikembangkan untuk memenuhi kebutuhan bisnis hortikultura. Kami memproduksi dan memasok tanaman eksotis Indonesia ke seluruh dunia — berfokus pada tanaman asli Indonesia berkualitas tinggi, dalam bentuk bare root dan kultur jaringan.", "Minaqu Indonesia is a supplier of tropical ornamental plants from Indonesia, developed to meet the needs of horticulture businesses. We produce and supply Indonesian exotic plants worldwide — focusing on high-quality native Indonesian plants, as bare root and tissue culture.")}</p>
        <p className="mt-4 text-[var(--muted-ink)] leading-relaxed">{t("Tanaman kultur jaringan umumnya bebas dari patogen, hama, dan penyakit yang dapat menyerang tanaman hasil perbanyakan tradisional. Kondisi steril di laboratorium menjamin bahan awal yang bersih.", "Tissue culture plants are generally free from pathogens, pests and diseases that can affect traditionally propagated plants. The sterile conditions in the laboratory guarantee clean starting material.")}</p>
        <div className="mt-8 grid grid-cols-3 gap-3">
          {["tc-lab-2", "tc-lab-4", "pack-3"].map((k) => <img key={k} src={`/assets/img/${k}.jpg`} alt="Laboratorium Minaqu" className="aspect-square w-full rounded-2xl object-cover" />)}
        </div>
      </Reveal>
    </div>
  </section>
  );
};

export default function Tissue() {
  const { t } = useLang();
  return (
    <>
      <PageHero testid="tissue-hero" tone="dark" eyebrow="Tissue Culture Collection · Wholesale Export" lines={["Tissue", "Culture", "Collection."]} img="/assets/img/tc-lab-1.jpg" imgAlt={t("Laboratorium kultur jaringan", "Tissue culture laboratory")}
        intro={t("46 varietas tanaman kultur jaringan asli Indonesia dengan kode produk dan harga USD per unit. Harga dapat dinegosiasikan untuk pembelian grosir.", "46 native Indonesian tissue culture varieties with product codes and USD unit prices. Prices can be negotiated for wholesale quantities.")} />
      <div className="bg-[var(--leaf)] py-5 text-white">
        <Marquee fast items={["Prices can be negotiated for wholesale quantities", "Bare root & tissue culture", "Export to 20+ countries"]} className="font-display text-xl sm:text-2xl font-extrabold" />
      </div>
      <Explainer />
      <section data-testid="tissue-list" className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 sm:py-24">
        <Reveal className="mb-12 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight">{t("Daftar varietas", "Variety list")} <span className="text-[var(--leaf)]">01–46</span></h2>
          <p className="text-sm text-[var(--muted-ink)]">{t("Setiap foto: tanaman pot (kiri) & plantlet bare root (kanan).", "Each photo: potted plant (left) & bare-root plantlet (right).")}</p>
        </Reveal>
        <div className="grid grid-cols-2 gap-x-4 gap-y-10 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.map((p, i) => <ProductCard key={p.id} p={p} code={p.code} index={i} />)}
        </div>
      </section>
    </>
  );
}
